import { defineStore } from "pinia";
import type { CategoryNode } from "../Api/Categories/categories";
import categoriesApi, { CategoryAgeLimit, CategoryFightTime, CategoryGender, CategoryDiscipline } from "../Api/Categories/categories";
import categoriesExtractionApi, { CategoriesExtractionPreview } from "../Api/Categories/categoriesExtraction";
import sessionStore from "../Common/sessionStore";
import { eventBus, OPEN_IMPORT_FROM_PAST_EVENT_MODAL } from "./eventBus";
import { countUndisciplinedLeaves } from "./categoriesUtils";

export type PathName = "import-template" | "extract-from-rules" | "import-from-previous-event" | "tree-builder" | null;

function getSessionKey(eventId: number): string {
    return `categories-manager-path-${eventId}`;
}

export const MAX_TEXT_CHARS = 15000;
export const MAX_EXTRACTIONS_PER_EVENT = 3;

export const useCategoriesManagerStore = defineStore("categoriesManager", {
    state: () => ({
        eventId: 0,
        isFullOrganizer: false,
        eventTimezone: "",
        currentUserId: null as number | null,

        editModeEnabled: 0,
        editedNodeName: "",
        addingModeEnabled: 0,
        moveModeEnabled: 0,
        categoriesModificationsEnabled: false,
        categoryTranslationKeys: {} as Record<string, string>,
        categoriesFlatTree: {} as Record<number, CategoryNode[]>,
        rootNode: null as CategoryNode | null,
        extendedNodes: {} as Record<number, boolean>,

        // Navigation state
        initDone: false,
        selectedPath: null as PathName | null,
        showTemplateCustomization: false,
        showExtractFromRules: false,
        showNewExtractionForm: false,

        // When true, a setup tool (e.g. template import) runs against an existing tree and
        // appends its output as a new named branch instead of creating the root.
        appendMode: false,

        //Extractions state
        extractions: [] as CategoriesExtractionPreview[],
        sourceText: "",
        currentExtractionId: null as string | null,
    }),

    getters: {
        isExpandedNode: state => {
            return (id: number) => {
                return state.extendedNodes[id];
            };
        },

        getChildrenForId: state => {
            return (id: number) => {
                return state.categoriesFlatTree[id];
            };
        },

        hasAnyChildren: state => {
            return (id: number) => {
                return state.categoriesFlatTree[id] && state.categoriesFlatTree[id].length > 0;
            };
        },

        getPrevSiblingForNodeId: state => {
            return (nodeId: number | null, parentId: number) => {
                const childrenForParent = state.categoriesFlatTree[parentId];

                if (nodeId === null) {
                    return childrenForParent[childrenForParent.length - 1];
                }

                for (let i = 0; i < childrenForParent.length; i++) {
                    if (childrenForParent[i].id === nodeId && i > 0) {
                        return childrenForParent[i - 1];
                    }
                }

                return null;
            };
        },

        getNextSiblingForNodeId: state => {
            return (nodeId: number, parentId: number) => {
                const childrenForParent = state.categoriesFlatTree[parentId];

                let sourceFound = false;

                for (let i = 0; i < childrenForParent.length; i++) {
                    if (sourceFound) {
                        return childrenForParent[i];
                    }

                    if (childrenForParent[i].id === nodeId) {
                        sourceFound = true;
                    }
                }

                return null;
            };
        },

        undisciplinedLeafCount: state => countUndisciplinedLeaves(state.categoriesFlatTree),

        totalCategoryCount: state =>
            Object.values(state.categoriesFlatTree)
                .flat()
                .filter(node => node.id !== 0 && !node.extendingNode).length,

        getBreadcrumbForId: state => {
            return (id: number): CategoryNode[] => {
                const nodesById = new Map(
                    Object.values(state.categoriesFlatTree)
                        .flat()
                        .map(node => [node.id, node]),
                );
                const chain: CategoryNode[] = [];
                let current = nodesById.get(id);
                while (current) {
                    chain.unshift(current);
                    current = current.parentId !== null ? nodesById.get(current.parentId) : undefined;
                }
                return chain;
            };
        },

        // Navigation getters
        hasCategoriesCreated: state => !!state.rootNode,
        hasOngoingOrDoneExtraction: state => state.extractions.length > 0,
        hasOngoingExtraction: state => {
            return state.extractions.some(extraction => extraction.status === "initiated" || extraction.status === "inProgress");
        },
        showFourPathScreen: state => state.selectedPath === null && !state.rootNode,
        showTreeBuilder: state => state.rootNode !== null,
        showImportFromTemplate: state => state.selectedPath === "import-template" && (!state.rootNode?.id || state.appendMode),

        //Extractions
        isExtractionSourceTextOverLimit: state => state.sourceText.length > MAX_TEXT_CHARS,
        remainingExtractions: state => Math.max(0, MAX_EXTRACTIONS_PER_EVENT - state.extractions.length),
        isExtractionLimitReached: state => state.extractions.length >= MAX_EXTRACTIONS_PER_EVENT,
    },

    actions: {
        moveNodeChild(payload: { nodeId: number; oldIndex: number; newIndex: number }) {
            const { nodeId, oldIndex, newIndex } = payload;
            const item = this.categoriesFlatTree[nodeId][oldIndex];
            this.categoriesFlatTree[nodeId].splice(oldIndex, 1);
            this.categoriesFlatTree[nodeId].splice(newIndex, 0, item);
        },

        extendNode(payload: { parentId: number }) {
            this.categoriesFlatTree[payload.parentId] = [
                {
                    id: 0,
                    parentId: payload.parentId,
                    name: "",
                    fightTime: {} as CategoryFightTime,
                    disciplineType: {} as CategoryDiscipline,
                    categoryGender: {} as CategoryGender,
                    categoryAgeLimit: {} as CategoryAgeLimit,
                    extendingNode: true,
                },
            ];
        },

        toggleNode(payload: number) {
            const current = !!this.extendedNodes[payload];
            this.extendedNodes[payload] = !current;
        },

        expandAllNodes() {
            Object.keys(this.categoriesFlatTree).forEach(key => {
                const params = this.categoriesFlatTree[Number(key)] ?? [];
                params.forEach(param => {
                    this.extendedNodes[param.id] = true;
                });
            });
        },

        collapseAllNodes() {
            Object.keys(this.categoriesFlatTree).forEach(key => {
                const params = this.categoriesFlatTree[Number(key)] ?? [];
                params.forEach(param => {
                    this.extendedNodes[param.id] = false;
                });
            });
        },

        refreshCategories() {
            return categoriesApi.getCategoriesFlatTree(this.eventId).then(categoriesFlatTreeData => {
                this.categoriesFlatTree = categoriesFlatTreeData;
                this.rootNode = (categoriesFlatTreeData[0] ?? [])[0] ?? null;

                // If categories were deleted while on tree builder, go back to path selection
                if (this.rootNode === null && this.selectedPath === "tree-builder") {
                    this.navigateTo(null);
                }
            });
        },

        refreshExtractions() {
            return categoriesExtractionApi.getExtractions(this.eventId).then(extractions => {
                this.extractions = extractions;
            });
        },

        // Navigation actions
        navigateTo(path: Exclude<PathName, "import-from-previous-event">) {
            // Reset all navigation state
            this.showTemplateCustomization = false;
            this.showExtractFromRules = false;
            this.showNewExtractionForm = false;
            this.currentExtractionId = null;
            this.appendMode = false;
            this.selectedPath = path;

            if (path === null || path === "tree-builder") {
                sessionStore.delete(getSessionKey(this.eventId));
            } else {
                sessionStore.set(getSessionKey(this.eventId), path);
            }

            if (path === "extract-from-rules") {
                this.showExtractFromRules = true;
                // Restore currentExtractionId for ongoing extractions
                const ongoingExtraction = this.extractions.find(e => e.status === "initiated" || e.status === "inProgress");
                if (ongoingExtraction) {
                    this.currentExtractionId = ongoingExtraction.id;
                }
            }
        },

        initialize() {
            const promises: Promise<void>[] = [this.refreshCategories()];

            if (this.isFullOrganizer) {
                promises.push(this.refreshExtractions());
            }

            return Promise.all(promises)
                .then(() => {
                    if (this.hasCategoriesCreated) {
                        this.navigateTo("tree-builder");
                        return;
                    }

                    // Restore user's path choice from session storage
                    const savedPath = sessionStore.get(getSessionKey(this.eventId)) as PathName | null;
                    if (savedPath && savedPath !== "import-from-previous-event") {
                        this.navigateTo(savedPath);
                    }
                })
                .finally(() => {
                    this.initDone = true;
                });
        },

        selectPath(pathName: PathName): Promise<void> {
            if (pathName === "import-from-previous-event") {
                eventBus.$emit(OPEN_IMPORT_FROM_PAST_EVENT_MODAL);
                return Promise.resolve();
            }

            if (pathName === "tree-builder") {
                return categoriesApi
                    .createRoot(this.eventId)
                    .then(() => this.refreshCategories())
                    .then(() => this.navigateTo("tree-builder"));
            }

            this.navigateTo(pathName);
            return Promise.resolve();
        },
    },
});
